import React, { useState, useMemo, useCallback, useEffect } from 'react';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SingleSelect } from '@/components/common/SingleSelect';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  MultiSelect,
  MultiSelectOption,
} from '@/components/common/MultiSelect';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useProfitabilityReport,
  useUploadProfitabilityData,
  useValidateProfitabilityData,
  useProductFamilyDescriptions,
} from '@/api/rebates/useReports';
import useRebateAgreementYears from '@/api/rebates/useRebateAgreementYears';
import useDivisions from '@/api/organization/useDivisions';
import useAllSuppliers from '@/api/accounts-payable/useAllSuppliersQuery';
import useCoreEnums from '@/api/core/core-enums/useCoreEnums';
import { REBATES_SYSTEM_ENUMS } from '@erp/shared-types';
import { TableSkeleton } from '@/components/common/loading-skeletons/table-skeleton';
import FileUploadDropzone from '@/components/documents/FileUploadDropzone';
import { UploadProgress, UploadStatus } from 'src/types/UserDocuments';
import type {
  SupplierSalesMarginUploadResponse,
  SupplierSalesMarginValidateResponse,
  ProfitabilityReportRow,
  UnmatchedBreakdown,
} from '@erp/shared-types';
import {
  UploadIcon,
  CircleNotchIcon,
  TrashIcon,
  DownloadIcon,
  CaretDownIcon,
  ShareNetworkIcon,
  LinkIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { ShareReportModal } from 'src/components/reports/ShareReportModal';
import { SharedLinksManager } from 'src/components/reports/SharedLinksManager';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { downloadProfitabilityReportCsv } from '@/api/rebates/useRebateDataExports';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from 'src/context/ToastContext';
import BreakdownTable, {
  type BreakdownCellContext,
} from '@/components/ui/breakdown-tables/BreakdownTable';
import {
  TreeTable,
  type ColumnDef,
} from '@/components/common/tree-table/TreeTable';
import { formatCurrencyAdvanced } from '@erp/shared-utils';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { deleteSalesMarginsForYear } from '@/api/rebates/useRebateDataExports';
import {
  AlertContainer,
  AlertTitle,
  AlertDescription,
} from '@/components/ui/alert';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

const REPORT_ID = 'profitability-report';
const REPORT_TITLE = 'Profitability Report';

// Columns that should NOT be aggregated when rows are expanded (percentages and text)
// These preserve their original values instead of being summed
const NO_AGGREGATE_COLUMNS = [
  'Internal Codes',
  'Landed Margin (%)',
  'Non-Conditional (%)',
  'Conditional (%)',
  'Redistribution (%)',
  'Total Rebates (%)',
  'Total Profit (%)',
];

// Map column header names to row property names
const COLUMN_TO_PROPERTY_MAP: Record<string, keyof ProfitabilityReportRow> = {
  'Internal Codes': 'internalCode',
  'COGS ($)': 'cogs',
  'Landed Margin (%)': 'landedMarginPercent',
  'Non-Conditional (%)': 'nonConditionalPercent',
  'Conditional (%)': 'conditionalPercent',
  'Redistribution (%)': 'redistributionPercent',
  'Total Rebates ($)': 'totalRebates',
  'Total Rebates (%)': 'totalRebatesPercent',
  'Total Profit (%)': 'totalProfitPercent',
};

// Format currency with 0 decimal places for whole dollar amounts
const formatCurrencyWhole = (value: number): string => {
  return formatCurrencyAdvanced(value, 'USD', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const formatPercent = (value: number): string => {
  return `${value.toFixed(2)}%`;
};

/**
 * Format date range from YYYY-MM format to readable month range
 * e.g., "2024-01" and "2024-05" -> "Jan - May"
 * e.g., "2024-01" and "2024-12" -> "Jan - Dec"
 */
const formatDateRange = (
  minYearMonth: string,
  maxYearMonth: string,
): string => {
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const parseYearMonth = (
    yearMonth: string,
  ): { year: number; month: number } => {
    const [year, month] = yearMonth.split('-').map(Number);
    return { year, month };
  };

  const min = parseYearMonth(minYearMonth);
  const max = parseYearMonth(maxYearMonth);

  const minMonthName = monthNames[min.month - 1];
  const maxMonthName = monthNames[max.month - 1];

  // If same year, just show month range
  if (min.year === max.year) {
    return `${minMonthName} - ${maxMonthName}`;
  }

  // If different years, show year and month
  return `${minMonthName} ${min.year} - ${maxMonthName} ${max.year}`;
};

/**
 * Aggregated unmatched data by supplier + division
 */
interface AggregatedUnmatchedEntry {
  supplierId: string;
  supplierName: string;
  divisionId: string;
  divisionName: string;
  totalRebateAmount: number;
  entryCount: number;
}

/**
 * Data Quality Notice component
 * Shows an expandable alert with details about unmatched rebate breakdowns
 */
const DataQualityNotice: React.FC<{
  unmatchedBreakdowns: UnmatchedBreakdown[];
}> = ({ unmatchedBreakdowns }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  // Aggregate by supplier + division and calculate totals
  const { aggregatedEntries, totalAmount } = React.useMemo(() => {
    const aggregationMap = new Map<string, AggregatedUnmatchedEntry>();
    let total = 0;

    for (const breakdown of unmatchedBreakdowns) {
      const key = `${breakdown.supplierId}|${breakdown.divisionId}`;
      const amount = Math.abs(breakdown.rebateAmount);
      total += amount;

      const existing = aggregationMap.get(key);
      if (existing) {
        existing.totalRebateAmount += amount;
        existing.entryCount += 1;
      } else {
        aggregationMap.set(key, {
          supplierId: breakdown.supplierId,
          supplierName: breakdown.supplierName,
          divisionId: breakdown.divisionId,
          divisionName: breakdown.divisionName,
          totalRebateAmount: amount,
          entryCount: 1,
        });
      }
    }

    // Sort by supplier name, then division name
    const entries = Array.from(aggregationMap.values()).sort((a, b) => {
      const supplierCompare = a.supplierName.localeCompare(b.supplierName);
      if (supplierCompare !== 0) return supplierCompare;
      return a.divisionName.localeCompare(b.divisionName);
    });

    return { aggregatedEntries: entries, totalAmount: total };
  }, [unmatchedBreakdowns]);

  // Group entries by supplier for display
  const groupedBySupplier = React.useMemo(() => {
    const groups: Array<{
      supplierName: string;
      entries: AggregatedUnmatchedEntry[];
    }> = [];
    let currentSupplier: string | null = null;
    let currentGroup: AggregatedUnmatchedEntry[] = [];

    for (const entry of aggregatedEntries) {
      if (entry.supplierId !== currentSupplier) {
        if (currentGroup.length > 0) {
          groups.push({
            supplierName: currentGroup[0].supplierName,
            entries: currentGroup,
          });
        }
        currentSupplier = entry.supplierId;
        currentGroup = [entry];
      } else {
        currentGroup.push(entry);
      }
    }
    if (currentGroup.length > 0) {
      groups.push({
        supplierName: currentGroup[0].supplierName,
        entries: currentGroup,
      });
    }

    return groups;
  }, [aggregatedEntries]);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <div className="flex items-center gap-3 w-full cursor-pointer rounded-lg border border-yellow-200 bg-yellow-50 hover:bg-yellow-100 transition-colors px-4 py-3">
          <WarningIcon className="h-4 w-4 text-yellow-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-yellow-800">
              Data Quality Notice
            </div>
            <div className="text-sm text-yellow-700">
              {aggregatedEntries.length} supplier/division{' '}
              {aggregatedEntries.length === 1 ? 'combination' : 'combinations'}{' '}
              ({formatCurrencyWhole(totalAmount)}) could not be matched to sales
              margin data. Click to view details.
            </div>
          </div>
          <CaretDownIcon
            className={`h-4 w-4 text-yellow-600 transition-transform flex-shrink-0 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-2 border border-yellow-200 rounded-md bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-yellow-50">
                <TableHead className="text-yellow-800">Supplier</TableHead>
                <TableHead className="text-yellow-800">Division</TableHead>
                <TableHead className="text-yellow-800 text-right">
                  Rebate Amount
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groupedBySupplier.map((group) =>
                group.entries.map((entry, idx) => (
                  <TableRow
                    key={`${entry.supplierId}-${entry.divisionId}`}
                    className="hover:bg-yellow-50"
                  >
                    <TableCell className="font-medium">
                      {idx === 0 ? group.supplierName : ''}
                    </TableCell>
                    <TableCell>{entry.divisionName}</TableCell>
                    <TableCell className="text-right">
                      {formatCurrencyWhole(entry.totalRebateAmount)}
                    </TableCell>
                  </TableRow>
                )),
              )}
            </TableBody>
          </Table>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

const ProfitabilityReportPage: React.FC = () => {
  const { showToast } = useToast();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // 1-12
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null); // null = YTD
  const [viewMode, setViewMode] = useState<
    'supplier' | 'division' | 'productFamilyDesc' | 'divisionHierarchy'
  >('supplier');
  const [selectedDivisions, setSelectedDivisions] = useState<
    MultiSelectOption[]
  >([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState<
    MultiSelectOption[]
  >([]);
  const [selectedProductFamilyDescs, setSelectedProductFamilyDescs] = useState<
    MultiSelectOption[]
  >([]);
  const [selectedShipTypes, setSelectedShipTypes] = useState<
    MultiSelectOption[]
  >([
    { id: '1', label: 'Ship Type 1', value: 1 },
    { id: '2', label: 'Ship Type 2', value: 2 },
    { id: '3', label: 'Ship Type 3', value: 3 },
  ]); // Default: all ship types selected
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [uploadErrors, setUploadErrors] = useState<
    Array<{ row: number; message: string }>
  >([]);
  // State for validation confirmation flow
  const [validationResult, setValidationResult] =
    useState<SupplierSalesMarginValidateResponse | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [uploadYear, setUploadYear] = useState<number>(selectedYear);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [linksManagerOpen, setLinksManagerOpen] = useState(false);

  // Sync uploadYear with selectedYear when it changes (when modal is closed)
  useEffect(() => {
    if (!isUploadModalOpen) {
      setUploadYear(selectedYear);
    }
  }, [selectedYear, isUploadModalOpen]);

  // Fetch divisions for filter
  const { data: divisionsData, isLoading: divisionsLoading } =
    useDivisions(true);

  // Fetch suppliers for filter
  const { data: suppliersData, isLoading: suppliersLoading } = useAllSuppliers(
    undefined,
    true,
  );

  // Fetch product family descriptions for filter
  const { data: productFamilyDescriptions, isLoading: prodFamilyDescsLoading } =
    useProductFamilyDescriptions();

  // Fetch available years for the year selector
  const { data: availableYearsData } = useRebateAgreementYears();
  const availableYears =
    availableYearsData && 'years' in availableYearsData
      ? availableYearsData.years
      : undefined;

  // Prepare year options - descending order, no future years
  const yearOptions = useMemo(() => {
    if (!availableYears || availableYears.length === 0) {
      // Fallback to last 5 years if no years available
      const years: number[] = [];
      for (let i = 0; i < 5; i++) {
        years.push(currentYear - i);
      }
      return years.map((year) => ({
        id: year.toString(),
        label: year.toString(),
        value: year,
      }));
    }
    return availableYears
      .filter((year) => year <= currentYear) // Exclude future years
      .sort((a, b) => b - a) // Descending order
      .map((year) => ({
        id: year.toString(),
        label: year.toString(),
        value: year,
      }));
  }, [availableYears, currentYear]);

  // Prepare division options
  const divisionOptions: MultiSelectOption[] = useMemo(() => {
    if (!divisionsData?.divisions) return [];
    return divisionsData.divisions.map((division) => ({
      id: division.id,
      label: division.name,
      value: division,
      group: division.tags?.[0]?.name,
    }));
  }, [divisionsData?.divisions]);

  // Prepare supplier options
  const supplierOptions: MultiSelectOption[] = useMemo(() => {
    if (!suppliersData) return [];
    return suppliersData.map((supplier) => ({
      id: supplier.id,
      label: supplier.name,
      value: supplier,
    }));
  }, [suppliersData]);

  // Prepare product family description options
  const productFamilyDescOptions: MultiSelectOption[] = useMemo(() => {
    if (!productFamilyDescriptions) return [];
    return productFamilyDescriptions.map((desc) => ({
      id: desc,
      label: desc,
      value: desc,
    }));
  }, [productFamilyDescriptions]);

  // Ship type options
  const shipTypeOptions: MultiSelectOption[] = useMemo(() => {
    return [
      { id: '1', label: 'Ship Type 1', value: 1 },
      { id: '2', label: 'Ship Type 2', value: 2 },
      { id: '3', label: 'Ship Type 3', value: 3 },
    ];
  }, []);

  // Prepare month options (YTD at top, then Jan through Dec in order)
  const monthOptions = useMemo(() => {
    // Start with YTD option
    const options = [{ id: 'ytd', label: 'YTD', value: null as number | null }];

    // Add months in order (January through December)
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    // Only show months up to current month if it's the current year
    const isCurrentYear = selectedYear === currentYear;
    const maxMonth = isCurrentYear ? currentMonth : 12;

    for (let i = 1; i <= maxMonth; i++) {
      options.push({
        id: i.toString(),
        label: monthNames[i - 1],
        value: i,
      });
    }

    return options;
  }, [selectedYear, currentYear, currentMonth]);

  // Validation mutation
  const validateMutation = useValidateProfitabilityData();

  // Upload mutation
  const uploadMutation = useUploadProfitabilityData();

  // Fetch report data
  const {
    data: report,
    isLoading,
    error,
    refetch,
  } = useProfitabilityReport({
    year: selectedYear,
    viewMode,
    month: selectedMonth,
    divisionIds:
      selectedDivisions.length > 0
        ? selectedDivisions.map((d) => d.id)
        : undefined,
    supplierIds:
      selectedSuppliers.length > 0
        ? selectedSuppliers.map((s) => s.id)
        : undefined,
    productFamilyDescs:
      selectedProductFamilyDescs.length > 0
        ? selectedProductFamilyDescs.map((p) => p.value)
        : undefined,
    shipTypes:
      selectedShipTypes.length > 0
        ? selectedShipTypes.map((s) => s.value)
        : undefined,
  });

  /**
   * Perform the actual upload, optionally skipping unmatched rows
   */
  const performUpload = async (file: File, skipUnmatched: boolean) => {
    setUploadProgress([
      {
        documentId: `temp-${Date.now()}`,
        fileName: file.name,
        progress: 50,
        status: UploadStatus.uploading,
      },
    ]);

    try {
      const result = await uploadMutation.mutateAsync({
        file,
        skipUnmatched,
        year: uploadYear,
      });
      if (result.data?.success) {
        setUploadProgress([
          {
            documentId: `temp-${Date.now()}`,
            fileName: file.name,
            progress: 100,
            status: UploadStatus.uploaded,
          },
        ]);
        const skippedMsg =
          result.data.rowsSkipped && result.data.rowsSkipped > 0
            ? ` Skipped: ${result.data.rowsSkipped}.`
            : '';
        showToast({
          title: 'Upload Successful',
          message: `Processed ${result.data.rowsProcessed} rows. Inserted: ${result.data.rowsInserted}, Updated: ${result.data.rowsUpdated}.${skippedMsg}`,
          color: 'success',
        });
        setIsUploadModalOpen(false);
        setUploadProgress([]);
        setValidationResult(null);
        setPendingFile(null);
        // Refetch report data after successful upload
        await refetch();
      } else if (result.data && !result.data.success) {
        setUploadProgress([
          {
            documentId: `temp-${Date.now()}`,
            fileName: file.name,
            progress: 0,
            status: UploadStatus.failed,
            error: 'Upload failed',
          },
        ]);
        setUploadErrors(result.data.errors || []);
      }
    } catch (err: unknown) {
      console.error('Upload error:', err);
      setUploadProgress([
        {
          documentId: `temp-${Date.now()}`,
          fileName: file.name,
          progress: 0,
          status: UploadStatus.failed,
          error: err instanceof Error ? err.message : 'Upload failed',
        },
      ]);

      // Try to extract errors from the error response
      const apiError = err as {
        responseData?: { data?: SupplierSalesMarginUploadResponse };
      };
      if (apiError.responseData?.data?.errors) {
        setUploadErrors(apiError.responseData.data.errors);
      } else if (
        uploadMutation.data?.data &&
        !uploadMutation.data.data.success
      ) {
        setUploadErrors(uploadMutation.data.data.errors || []);
      } else {
        setUploadErrors([
          {
            row: 0,
            message:
              err instanceof Error
                ? err.message
                : 'Upload failed. Please check the file format and try again.',
          },
        ]);
      }
    }
  };

  /**
   * Handle CSV download - downloads the current report as CSV
   */
  const handleDownloadCsv = useCallback(async () => {
    try {
      const filters: Record<string, unknown> = {
        year: selectedYear,
        viewMode,
        month: selectedMonth,
      };

      // Add optional filters if they have values
      if (selectedDivisions.length > 0) {
        filters.divisionIds = selectedDivisions.map((d) => d.id).join(',');
      }
      if (selectedSuppliers.length > 0) {
        filters.supplierIds = selectedSuppliers.map((s) => s.id).join(',');
      }
      if (selectedProductFamilyDescs.length > 0) {
        filters.productFamilyDescs = selectedProductFamilyDescs
          .map((p) => p.value)
          .join(',');
      }
      if (selectedShipTypes.length > 0) {
        filters.shipTypes = selectedShipTypes.map((s) => s.value).join(',');
      }

      await downloadProfitabilityReportCsv(filters);
      showToast({
        message: 'CSV downloaded successfully',
        title: 'Success',
        color: 'success',
        icon: 'check',
      });
    } catch (error) {
      console.error('Failed to download CSV:', error);
      showToast({
        message:
          error instanceof Error ? error.message : 'Failed to download CSV',
        title: 'Error',
        color: 'error',
        icon: 'error',
      });
    }
  }, [
    selectedYear,
    viewMode,
    selectedMonth,
    selectedDivisions,
    selectedSuppliers,
    selectedProductFamilyDescs,
    selectedShipTypes,
    showToast,
  ]);

  /**
   * Handle delete confirmation - deletes all sales margins for the selected year
   */
  const handleDeleteConfirm = useCallback(async () => {
    setIsDeleting(true);
    try {
      const result = await deleteSalesMarginsForYear(selectedYear);
      if (result.success) {
        showToast({
          message: `Deleted ${result.deletedCount} sales margin record(s) for ${selectedYear}`,
          title: 'Success',
          color: 'success',
          icon: 'check',
        });
        // Refresh the report after deletion
        await refetch();
      }
    } catch (error) {
      console.error('Failed to delete sales margins:', error);
      showToast({
        message:
          error instanceof Error
            ? error.message
            : 'Failed to delete sales margins',
        title: 'Error',
        color: 'error',
        icon: 'error',
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  }, [selectedYear, refetch, showToast]);

  /**
   * Handle file selection - first validates, then shows confirmation if needed
   */
  const handleFilesSelected = async (files: File[]) => {
    if (files.length === 0) {
      return;
    }
    if (files.length > 1) {
      showToast({
        title: 'Error',
        message: 'Please upload only one file at a time.',
        color: 'error',
      });
      return;
    }

    const file = files[0];
    setUploadErrors([]);
    setValidationResult(null);
    setPendingFile(null);
    setUploadProgress([
      {
        documentId: `temp-${Date.now()}`,
        fileName: file.name,
        progress: 0,
        status: UploadStatus.uploading,
      },
    ]);

    try {
      // First, validate the file
      const validateResult = await validateMutation.mutateAsync({ file });

      if (validateResult.data?.valid) {
        // All rows matched - proceed with upload directly
        await performUpload(file, false);
      } else if (validateResult.data) {
        // There are unmatched rows - show confirmation dialog
        setValidationResult(validateResult.data);
        setPendingFile(file);
        setIsConfirmDialogOpen(true);
        setUploadProgress([]);
      }
    } catch (err: unknown) {
      console.error('Validation error:', err);
      setUploadProgress([
        {
          documentId: `temp-${Date.now()}`,
          fileName: file.name,
          progress: 0,
          status: UploadStatus.failed,
          error: err instanceof Error ? err.message : 'Validation failed',
        },
      ]);
      setUploadErrors([
        {
          row: 0,
          message:
            err instanceof Error
              ? err.message
              : 'Failed to validate file. Please check the file format and try again.',
        },
      ]);
    }
  };

  /**
   * User confirmed to proceed with upload, skipping unmatched rows
   */
  const handleConfirmUpload = async () => {
    setIsConfirmDialogOpen(false);
    if (pendingFile) {
      await performUpload(pendingFile, true);
    }
  };

  /**
   * User cancelled the upload after seeing unmatched rows
   */
  const handleCancelUpload = () => {
    setIsConfirmDialogOpen(false);
    setValidationResult(null);
    setPendingFile(null);
    setUploadProgress([]);
  };

  const columnHeaders = useMemo(
    () => [
      'Internal Codes',
      'COGS ($)',
      'Landed Margin (%)',
      'Non-Conditional (%)',
      'Conditional (%)',
      'Redistribution (%)',
      'Total Rebates ($)',
      'Total Rebates (%)',
      'Total Profit (%)',
    ],
    [],
  );

  // Get row label based on view mode
  const rowLabel = useMemo(() => {
    if (viewMode === 'supplier') return 'Supplier';
    if (viewMode === 'division') return 'Division';
    if (viewMode === 'divisionHierarchy') return 'Division';
    return 'Product Family Desc';
  }, [viewMode]);

  const subRowLabel = useMemo(() => {
    if (viewMode === 'supplier') return 'Division';
    if (viewMode === 'division') return 'Product Family';
    if (viewMode === 'divisionHierarchy') return 'Product Family';
    return 'Division';
  }, [viewMode]);

  // TreeTable columns for divisionHierarchy view mode
  // Row has: divisionName | productFamilyDesc | supplierName depending on depth
  const treeTableColumns = useMemo((): ColumnDef<ProfitabilityReportRow>[] => {
    return [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 280,
        cell: ({ row }) => {
          const data = row.original;
          // Determine the label based on available fields
          const label =
            data.divisionName ||
            data.productFamilyDesc ||
            data.supplierName ||
            '';
          return <span className="font-medium">{label}</span>;
        },
      },
      {
        accessorKey: 'internalCode',
        header: 'Internal Codes',
        size: 100,
        cell: ({ getValue }) => {
          const value = getValue() as string | undefined;
          if (!value) return <span className="text-muted-foreground">-</span>;
          return <span>{value}</span>;
        },
      },
      {
        accessorKey: 'cogs',
        header: 'COGS ($)',
        size: 100,
        cell: ({ getValue }) => {
          const value = getValue() as number;
          return <span>{formatCurrencyWhole(value)}</span>;
        },
      },
      {
        accessorKey: 'landedMarginPercent',
        header: 'Landed Margin (%)',
        size: 120,
        cell: ({ getValue }) => {
          const value = getValue() as number;
          return <span>{formatPercent(value)}</span>;
        },
      },
      {
        accessorKey: 'nonConditionalPercent',
        header: 'Non-Conditional (%)',
        size: 130,
        cell: ({ getValue }) => {
          const value = getValue() as number;
          return <span>{formatPercent(value)}</span>;
        },
      },
      {
        accessorKey: 'conditionalPercent',
        header: 'Conditional (%)',
        size: 110,
        cell: ({ getValue }) => {
          const value = getValue() as number;
          return <span>{formatPercent(value)}</span>;
        },
      },
      {
        accessorKey: 'redistributionPercent',
        header: 'Redistribution (%)',
        size: 120,
        cell: ({ getValue }) => {
          const value = getValue() as number;
          return <span>{formatPercent(value)}</span>;
        },
      },
      {
        accessorKey: 'totalRebates',
        header: 'Total Rebates ($)',
        size: 120,
        cell: ({ getValue }) => {
          const value = getValue() as number;
          return <span>{formatCurrencyWhole(value)}</span>;
        },
      },
      {
        accessorKey: 'totalRebatesPercent',
        header: 'Total Rebates (%)',
        size: 120,
        cell: ({ getValue }) => {
          const value = getValue() as number;
          return <span>{formatPercent(value)}</span>;
        },
      },
      {
        accessorKey: 'totalProfitPercent',
        header: 'Total Profit (%)',
        size: 110,
        cell: ({ getValue }) => {
          const value = getValue() as number;
          return <span className="font-semibold">{formatPercent(value)}</span>;
        },
      },
    ];
  }, []);

  // Column options for the sort dropdown
  const columnOptions = useMemo(() => {
    const rowLabelOption = {
      id: '__row_label__',
      label: rowLabel,
      value: '__row_label__',
    };

    const metricOptions = columnHeaders.map((col) => ({
      id: col,
      label: col,
      value: col,
    }));

    return [rowLabelOption, ...metricOptions];
  }, [columnHeaders, rowLabel]);

  // Sort report rows by selected column (descending for numeric, ascending for alphabetic)
  const sortedReportRows = useMemo(() => {
    if (!report || report.rows.length === 0) {
      return [];
    }
    if (!selectedColumn) {
      return report.rows;
    }

    // Helper to get the label for a row based on its level in the hierarchy
    const getRowLabel = (
      row: ProfitabilityReportRow,
      level: 0 | 1 | 2,
    ): string => {
      if (viewMode === 'divisionHierarchy') {
        // Level 0: Division, Level 1: Product Family, Level 2: Supplier
        if (level === 0) return row.divisionName || '';
        if (level === 1) return row.productFamilyDesc || '';
        return row.supplierName || '';
      }
      // 2-level modes
      if (level === 0) {
        return viewMode === 'supplier'
          ? row.supplierName || ''
          : viewMode === 'division'
            ? row.divisionName || ''
            : row.productFamilyDesc || '';
      }
      // Level 1 (children)
      return viewMode === 'supplier'
        ? row.divisionName || ''
        : viewMode === 'division'
          ? row.productFamilyDesc || ''
          : row.divisionName || '';
    };

    // Helper to sort children recursively (for 3-level hierarchy)
    const sortChildrenRecursively = (
      rows: ProfitabilityReportRow[],
      level: 0 | 1 | 2,
      compareFn: (
        a: ProfitabilityReportRow,
        b: ProfitabilityReportRow,
      ) => number,
    ): ProfitabilityReportRow[] => {
      const sorted = [...rows].sort(compareFn);
      return sorted.map((row) => {
        if (row.children && row.children.length > 0 && level < 2) {
          return {
            ...row,
            children: sortChildrenRecursively(
              row.children,
              (level + 1) as 0 | 1 | 2,
              compareFn,
            ),
          };
        }
        return row;
      });
    };

    // Handle alphabetical sort by row label
    if (selectedColumn === '__row_label__') {
      if (viewMode === 'divisionHierarchy') {
        // For 3-level hierarchy, sort each level alphabetically by its own label
        const sortLevel = (
          rows: ProfitabilityReportRow[],
          level: 0 | 1 | 2,
        ): ProfitabilityReportRow[] => {
          const sorted = [...rows].sort((a, b) => {
            const aLabel = getRowLabel(a, level);
            const bLabel = getRowLabel(b, level);
            return aLabel.localeCompare(bLabel);
          });
          return sorted.map((row) => {
            if (row.children && row.children.length > 0 && level < 2) {
              return {
                ...row,
                children: sortLevel(row.children, (level + 1) as 0 | 1 | 2),
              };
            }
            return row;
          });
        };
        return sortLevel(report.rows, 0);
      }

      // 2-level modes
      const sortedRows = [...report.rows].sort((a, b) => {
        const aLabel = getRowLabel(a, 0);
        const bLabel = getRowLabel(b, 0);
        return aLabel.localeCompare(bLabel);
      });

      return sortedRows.map((row) => {
        if (row.children && row.children.length > 0) {
          const sortedChildren = [...row.children].sort((a, b) => {
            const aLabel = getRowLabel(a, 1);
            const bLabel = getRowLabel(b, 1);
            return aLabel.localeCompare(bLabel);
          });
          return { ...row, children: sortedChildren };
        }
        return row;
      });
    }

    const propertyName = COLUMN_TO_PROPERTY_MAP[selectedColumn];
    if (!propertyName) {
      return report.rows;
    }

    // Numeric comparison function (descending - largest first)
    const numericCompare = (
      a: ProfitabilityReportRow,
      b: ProfitabilityReportRow,
    ) => {
      const aValue = (a[propertyName] as number) ?? 0;
      const bValue = (b[propertyName] as number) ?? 0;
      return bValue - aValue;
    };

    // For 3-level hierarchy, sort all levels
    if (viewMode === 'divisionHierarchy') {
      return sortChildrenRecursively(report.rows, 0, numericCompare);
    }

    // 2-level modes
    const sortedRows = [...report.rows].sort(numericCompare);

    return sortedRows.map((row) => {
      if (row.children && row.children.length > 0) {
        const sortedChildren = [...row.children].sort(numericCompare);
        return { ...row, children: sortedChildren };
      }
      return row;
    });
  }, [report, selectedColumn, viewMode]);

  // Add unique compound IDs to rows for TreeTable (prevents duplicate key issues)
  // Creates IDs like: "div-123", "div-123__pf-WIRING DEVICES", "div-123__pf-WIRING DEVICES__sup-456"
  type RowWithUniqueId = ProfitabilityReportRow & { _uniqueId: string };
  const rowsWithUniqueIds = useMemo((): RowWithUniqueId[] => {
    if (viewMode !== 'divisionHierarchy') {
      return sortedReportRows as RowWithUniqueId[];
    }

    return sortedReportRows.map((divisionRow): RowWithUniqueId => {
      const divisionId = `div-${divisionRow.divisionId || 'unknown'}`;
      const productFamilyChildren = divisionRow.children?.map((pfRow) => {
        const pfId = `${divisionId}__pf-${pfRow.productFamilyDesc || 'unknown'}`;
        const supplierChildren = pfRow.children?.map((supplierRow) => ({
          ...supplierRow,
          _uniqueId: `${pfId}__sup-${supplierRow.supplierId || 'unknown'}`,
        }));
        return {
          ...pfRow,
          _uniqueId: pfId,
          children: supplierChildren,
        };
      });
      return {
        ...divisionRow,
        _uniqueId: divisionId,
        children: productFamilyChildren,
      };
    });
  }, [sortedReportRows, viewMode]);

  // Extract row order from sorted rows to preserve sort order in BreakdownTable
  const yDimOrder = useMemo(() => {
    if (!sortedReportRows || sortedReportRows.length === 0) {
      return undefined;
    }
    return sortedReportRows.map((row) => {
      if (viewMode === 'supplier') {
        return row.supplierName || '';
      } else if (viewMode === 'division') {
        return row.divisionName || '';
      } else {
        return row.productFamilyDesc || '';
      }
    });
  }, [sortedReportRows, viewMode]);

  // Extract sub-row order from sorted rows to preserve sort order in BreakdownTable
  const yDim2Order = useMemo(() => {
    if (!sortedReportRows || sortedReportRows.length === 0) {
      return undefined;
    }
    // Collect all sub-rows in the order they appear (sorted within each parent)
    const subRowOrder: string[] = [];
    sortedReportRows.forEach((row) => {
      if (row.children && row.children.length > 0) {
        row.children.forEach((childRow) => {
          const subRowLabel =
            viewMode === 'supplier'
              ? childRow.divisionName || ''
              : viewMode === 'division'
                ? childRow.productFamilyDesc || ''
                : childRow.divisionName || '';
          // Only add if not already in the array (to avoid duplicates)
          if (!subRowOrder.includes(subRowLabel)) {
            subRowOrder.push(subRowLabel);
          }
        });
      }
    });
    return subRowOrder.length > 0 ? subRowOrder : undefined;
  }, [sortedReportRows, viewMode]);

  // Transform profitability report data into BreakdownTable format using sorted rows
  const breakdownData = useMemo(() => {
    if (!report || sortedReportRows.length === 0) {
      return [];
    }

    const cells: Array<{
      column: string;
      subColumn: null;
      row: string;
      subRow: string | null;
      value: number;
      metadata?: Record<string, unknown>;
    }> = [];

    const columnHeaders = [
      'Internal Codes',
      'COGS ($)',
      'Landed Margin (%)',
      'Non-Conditional (%)',
      'Conditional (%)',
      'Redistribution (%)',
      'Total Rebates ($)',
      'Total Rebates (%)',
      'Total Profit (%)',
    ];

    // Iterate through each primary row (now sorted)
    sortedReportRows.forEach((row) => {
      const primaryRowLabel =
        viewMode === 'supplier'
          ? row.supplierName || ''
          : viewMode === 'division'
            ? row.divisionName || ''
            : row.productFamilyDesc || '';

      // Add cells for primary row (aggregated totals)
      const primaryValues = [
        0, // Internal Codes - will use metadata.formattedValue
        row.cogs,
        row.landedMarginPercent,
        row.nonConditionalPercent,
        row.conditionalPercent,
        row.redistributionPercent,
        row.totalRebates,
        row.totalRebatesPercent,
        row.totalProfitPercent,
      ];

      columnHeaders.forEach((column, colIdx) => {
        const cellData: {
          column: string;
          subColumn: null;
          row: string;
          subRow: null;
          value: number;
          metadata?: Record<string, unknown>;
        } = {
          column,
          subColumn: null,
          row: primaryRowLabel,
          subRow: null,
          value: primaryValues[colIdx],
          metadata: { lineType: 'reported' },
        };

        // For Internal Codes column, add the code as formattedValue
        if (column === 'Internal Codes') {
          cellData.metadata = {
            ...cellData.metadata,
            formattedValue: row.internalCode || '',
          };
        }

        cells.push(cellData);
      });

      // Add cells for secondary rows (children)
      if (row.children && row.children.length > 0) {
        row.children.forEach((childRow) => {
          const secondaryRowLabel =
            viewMode === 'supplier'
              ? childRow.divisionName || ''
              : viewMode === 'division'
                ? childRow.productFamilyDesc || ''
                : childRow.divisionName || '';

          const secondaryValues = [
            0, // Internal Codes - will use metadata.formattedValue
            childRow.cogs,
            childRow.landedMarginPercent,
            childRow.nonConditionalPercent,
            childRow.conditionalPercent,
            childRow.redistributionPercent,
            childRow.totalRebates,
            childRow.totalRebatesPercent,
            childRow.totalProfitPercent,
          ];

          columnHeaders.forEach((column, colIdx) => {
            const cellData: {
              column: string;
              subColumn: null;
              row: string;
              subRow: string;
              value: number;
              metadata?: Record<string, unknown>;
            } = {
              column,
              subColumn: null,
              row: primaryRowLabel,
              subRow: secondaryRowLabel,
              value: secondaryValues[colIdx],
              metadata: { lineType: 'reported' },
            };

            // For Internal Codes column, add the code as formattedValue
            if (column === 'Internal Codes') {
              cellData.metadata = {
                ...cellData.metadata,
                formattedValue: childRow.internalCode || '',
              };
            }

            cells.push(cellData);
          });
        });
      }
    });

    return cells;
  }, [sortedReportRows, viewMode, report]);

  // Custom cell renderer for BreakdownTable
  const renderCell = useCallback(
    (context: BreakdownCellContext): React.ReactNode => {
      const { cell, column } = context;
      const value = cell.value;
      const cellLineType = cell.metadata?.lineType as string | undefined;

      // Handle Internal Codes column specially - display as text
      if (column === 'Internal Codes') {
        const codeValue = (cell.metadata?.formattedValue as string) || '';
        if (!codeValue) {
          return <span className="text-xs text-muted-foreground">-</span>;
        }
        return (
          <div className="text-xs font-medium text-foreground">{codeValue}</div>
        );
      }

      // Handle null/undefined values
      if (value === null || value === undefined) {
        return <span className="text-xs text-muted-foreground">-</span>;
      }

      // Extract numeric value
      const numValue = typeof value === 'number' ? value : 0;

      // Determine color based on lineType - default to black if not specified
      let textColor = 'text-foreground';
      if (cellLineType === 'predictions') {
        textColor = 'text-blue-600';
      } else if (cellLineType === 'estimates') {
        textColor = 'text-green-600';
      }
      // 'reported' or undefined/null -> black (text-foreground)

      // Determine if this is a percentage column (contains '%') or currency column (contains '$')
      const isPercentColumn = column.includes('%');
      const isTotalProfitColumn = column === 'Total Profit (%)';

      // Format value
      const formattedValue = isPercentColumn
        ? formatPercent(numValue)
        : formatCurrencyWhole(numValue);

      return (
        <div
          className={`text-xs font-medium ${textColor} ${
            isTotalProfitColumn ? 'font-semibold' : ''
          }`}
        >
          {formattedValue}
        </div>
      );
    },
    [],
  );

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">{REPORT_TITLE}</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Upload supplier sales margin data and view profitability analysis
            </p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <ShareNetworkIcon className="h-4 w-4 mr-2" />
                Share
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setShareModalOpen(true)}>
                <ShareNetworkIcon className="h-4 w-4 mr-2" />
                Create share link
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLinksManagerOpen(true)}>
                <LinkIcon className="h-4 w-4 mr-2" />
                Manage shared links
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Upload, Download, and Delete Buttons */}
        <div className="mb-6 flex gap-2">
          <Button
            onClick={() => {
              setUploadYear(selectedYear); // Set upload year to currently selected year
              setIsUploadModalOpen(true);
            }}
          >
            <UploadIcon className="h-4 w-4 mr-2" />
            Upload Sales Margin Data
          </Button>
          <Button onClick={handleDownloadCsv} variant="outline">
            <DownloadIcon className="h-4 w-4 mr-2" />
            Download CSV
          </Button>
          <Button
            onClick={() => setShowDeleteDialog(true)}
            variant="outline"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <TrashIcon className="h-4 w-4 mr-2" />
            Delete {selectedYear} Data
          </Button>
        </div>

        {/* Upload Modal */}
        <Dialog open={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Upload Sales Margin Data</DialogTitle>
              <DialogDescription>
                Upload an Excel file (.xlsx) containing supplier sales margin
                data. The file should have a header row starting with 'Corporate
                Supplier1 Number'.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <AlertContainer className="border-red-200 bg-red-50">
                <WarningIcon className="h-4 w-4 text-red-600" />
                <AlertTitle className="text-red-800">
                  Data Replacement Warning
                </AlertTitle>
                <AlertDescription className="text-red-700">
                  Uploading data for {uploadYear} will replace all existing
                  sales margin data for that year. This action cannot be undone.
                </AlertDescription>
              </AlertContainer>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Year:</label>
                <SingleSelect
                  options={yearOptions}
                  value={
                    yearOptions.find((opt) => opt.value === uploadYear) || null
                  }
                  onChange={(option) => {
                    if (option) {
                      setUploadYear(option.value);
                    }
                  }}
                  placeholder="Select year"
                  shouldAutoSort={false}
                />
              </div>
              <FileUploadDropzone
                onFilesSelected={handleFilesSelected}
                uploadProgress={uploadProgress}
                isUploading={
                  uploadMutation.isLoading || validateMutation.isLoading
                }
                maxFiles={1}
                fillAvailableHeight={false}
              />

              {/* Errors Table */}
              {uploadErrors.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-lg font-semibold mb-2 text-destructive">
                    Upload Errors ({uploadErrors.length})
                  </h3>
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Row</TableHead>
                          <TableHead>Error Message</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {uploadErrors.map((error, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">
                              {error.row}
                            </TableCell>
                            <TableCell className="text-destructive">
                              {error.message}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsUploadModalOpen(false);
                  setUploadErrors([]);
                  setUploadProgress([]);
                  setUploadYear(selectedYear); // Reset to selected year when closing
                }}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirmation Dialog for Unmatched Suppliers */}
        <Dialog
          open={isConfirmDialogOpen}
          onOpenChange={setIsConfirmDialogOpen}
        >
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Unmatched Supplier Codes Found</DialogTitle>
              <DialogDescription>
                Some rows in your file have supplier codes that don't match any
                existing suppliers. You can continue and skip these rows, or
                cancel to fix the file.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {validationResult && (
                <>
                  <div className="text-sm space-y-1">
                    <p>
                      <strong>Total rows:</strong> {validationResult.totalRows}
                    </p>
                    <p>
                      <strong>Rows that will be processed:</strong>{' '}
                      {validationResult.matchedRows}
                    </p>
                    <p>
                      <strong>Rows that will be skipped:</strong>{' '}
                      {validationResult.totalRows -
                        validationResult.matchedRows}
                    </p>
                  </div>

                  {validationResult.unmatchedSupplierCodes.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-sm mb-2">
                        Unmatched Supplier Codes (
                        {validationResult.unmatchedSupplierCodes.length})
                      </h4>
                      <div className="border rounded-md max-h-48 overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Supplier Code</TableHead>
                              <TableHead>Rows Affected</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {validationResult.unmatchedSupplierCodes.map(
                              (item, index) => (
                                <TableRow key={index}>
                                  <TableCell className="font-mono text-sm">
                                    {item.code}
                                  </TableCell>
                                  <TableCell>{item.rowCount}</TableCell>
                                </TableRow>
                              ),
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}

                  {validationResult.unmatchedDivisions.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-sm mb-2">
                        Unmatched Divisions (
                        {validationResult.unmatchedDivisions.length})
                      </h4>
                      <div className="border rounded-md max-h-48 overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Division Name</TableHead>
                              <TableHead>Rows Affected</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {validationResult.unmatchedDivisions.map(
                              (item, index) => (
                                <TableRow key={index}>
                                  <TableCell>{item.name}</TableCell>
                                  <TableCell>{item.rowCount}</TableCell>
                                </TableRow>
                              ),
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={handleCancelUpload}
                disabled={uploadMutation.isLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmUpload}
                disabled={uploadMutation.isLoading}
              >
                {uploadMutation.isLoading ? (
                  <>
                    <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  'Continue and Skip Unmatched'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Date Range Status Card */}
        {report?.dateRange && !isLoading && !error && (
          <Card className="mb-6 bg-blue-50 border-blue-200">
            <CardContent className="text-center p-4">
              <div className="text-xs font-bold text-muted-foreground mb-2">
                Supplier Sales Margins Date Range
              </div>
              <div className="text-lg font-bold text-black mb-1">
                {formatDateRange(
                  report.dateRange.minYearMonth,
                  report.dateRange.maxYearMonth,
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                Uploaded data coverage
              </div>
            </CardContent>
          </Card>
        )}

        {/* Report Section */}
        {isLoading ? (
          <Card>
            <CardHeader>
              <CardTitle>Profitability Report</CardTitle>
            </CardHeader>
            <CardContent>
              <TableSkeleton />
            </CardContent>
          </Card>
        ) : error ? (
          <Card>
            <CardHeader>
              <CardTitle>Profitability Report</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-destructive">
                Error loading report: {error.message}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Profitability Report</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* View Mode Tabs */}
              <Tabs
                value={viewMode}
                onValueChange={(value) => {
                  const newMode = value as
                    | 'supplier'
                    | 'division'
                    | 'productFamilyDesc'
                    | 'divisionHierarchy';
                  setViewMode(newMode);
                  // Clear product family filter when switching to productFamilyDesc or divisionHierarchy view
                  // (filter is not applicable in these view modes)
                  if (
                    newMode === 'productFamilyDesc' ||
                    newMode === 'divisionHierarchy'
                  ) {
                    setSelectedProductFamilyDescs([]);
                  }
                  // Set default sort to division name for divisionHierarchy view
                  if (newMode === 'divisionHierarchy') {
                    setSelectedColumn('__row_label__');
                  }
                }}
              >
                <TabsList>
                  <TabsTrigger value="supplier">Supplier</TabsTrigger>
                  <TabsTrigger value="productFamilyDesc">
                    Product Family
                  </TabsTrigger>
                  <TabsTrigger value="divisionHierarchy">
                    Division Hierarchy
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              {/* Filters */}
              <div className="flex items-start gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium leading-10">
                    Year:
                  </label>
                  <SingleSelect
                    options={yearOptions}
                    value={
                      yearOptions.find((opt) => opt.value === selectedYear) ||
                      null
                    }
                    onChange={(option) => {
                      if (option) {
                        setSelectedYear(option.value);
                      }
                    }}
                    placeholder="Select year"
                    shouldAutoSort={false}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium leading-10">
                    Month:
                  </label>
                  <SingleSelect
                    options={monthOptions}
                    value={
                      monthOptions.find((opt) => opt.value === selectedMonth) ||
                      monthOptions[0]
                    }
                    onChange={(option) => {
                      if (option) {
                        setSelectedMonth(option.value);
                      }
                    }}
                    placeholder="Select month"
                    shouldAutoSort={false}
                  />
                </div>
                {viewMode === 'supplier' && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium leading-10">
                      Suppliers:
                    </label>
                    <MultiSelect
                      options={supplierOptions}
                      value={selectedSuppliers}
                      onChange={setSelectedSuppliers}
                      placeholder="All suppliers"
                      disabled={suppliersLoading}
                      hideChips={true}
                      liveUpdate={false}
                    />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium leading-10">
                    Divisions:
                  </label>
                  <MultiSelect
                    options={divisionOptions}
                    value={selectedDivisions}
                    onChange={setSelectedDivisions}
                    placeholder="All divisions"
                    disabled={divisionsLoading}
                    hideChips={true}
                    liveUpdate={false}
                  />
                </div>
                {/* Hide product family filter in productFamilyDesc and divisionHierarchy view modes */}
                {viewMode !== 'productFamilyDesc' &&
                  viewMode !== 'divisionHierarchy' && (
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium leading-10">
                        Product Family:
                      </label>
                      <MultiSelect
                        options={productFamilyDescOptions}
                        value={selectedProductFamilyDescs}
                        onChange={setSelectedProductFamilyDescs}
                        placeholder="All product families"
                        disabled={prodFamilyDescsLoading}
                        hideChips={true}
                        liveUpdate={false}
                      />
                    </div>
                  )}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium leading-10">
                    Ship Type:
                  </label>
                  <MultiSelect
                    options={shipTypeOptions}
                    value={selectedShipTypes}
                    onChange={setSelectedShipTypes}
                    placeholder="All ship types"
                    hideChips={true}
                    liveUpdate={false}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium leading-10">
                    Sort By:
                  </label>
                  <SingleSelect
                    options={columnOptions}
                    value={
                      columnOptions.find(
                        (opt) => opt.value === selectedColumn,
                      ) || null
                    }
                    onChange={(option) => {
                      setSelectedColumn(option?.value || null);
                    }}
                    placeholder="No sorting"
                    allowClear={true}
                    shouldAutoSort={false}
                  />
                </div>
              </div>

              {/* Data Quality Notice - shows unmatched breakdowns */}
              {report?.unmatchedBreakdowns &&
                report.unmatchedBreakdowns.length > 0 && (
                  <DataQualityNotice
                    unmatchedBreakdowns={report.unmatchedBreakdowns}
                  />
                )}

              {/* Report Table or Empty Message */}
              {viewMode === 'divisionHierarchy' ? (
                // TreeTable for 3-level hierarchy: Division -> Product Family -> Supplier
                sortedReportRows.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    No data available for the selected filters. Please upload
                    sales margin data or adjust your filters.
                  </div>
                ) : (
                  <TreeTable
                    data={rowsWithUniqueIds}
                    columns={treeTableColumns}
                    maxHeight={700}
                    defaultExpanded={false}
                    indentSize={24}
                  />
                )
              ) : breakdownData.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  No data available for the selected filters. Please upload
                  sales margin data or adjust your filters.
                </div>
              ) : (
                <BreakdownTable
                  data={breakdownData}
                  columnLabel="Metric"
                  rowLabel={rowLabel}
                  subRowLabel={subRowLabel}
                  title=""
                  xDimOrder={columnHeaders}
                  yDimOrder={yDimOrder}
                  yDim2Order={yDim2Order}
                  expandAllRows={false}
                  renderCell={renderCell}
                  showCard={false}
                  noAggregateColumns={NO_AGGREGATE_COLUMNS}
                />
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <ConfirmDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        title={`Delete ${selectedYear} Sales Margins`}
        message={`Are you sure you want to delete all sales margin data for ${selectedYear}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        loading={isDeleting}
        dangerous
      />

      <ShareReportModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        reportId={REPORT_ID}
        reportTitle={REPORT_TITLE}
      />
      <SharedLinksManager
        open={linksManagerOpen}
        onClose={() => setLinksManagerOpen(false)}
        reportId={REPORT_ID}
        reportTitle={REPORT_TITLE}
        onCreateNew={() => {
          setLinksManagerOpen(false);
          setShareModalOpen(true);
        }}
      />
    </DashboardLayout>
  );
};

export default ProfitabilityReportPage;
